import type { NextRequest } from "@/modules/fetch/types"
import type { I18nLocale } from "@/modules/i18n/types"

import { getResultFromResponse } from "@/modules/apiRequests/handlers"
import { getCookie } from "@/modules/client/cookies/getCookies"
import { ensureError } from "@/modules/fetch/errorHandler"
import { fetcher } from "@/modules/fetch/fetcher"
import { toCamelCaseKeys } from "@/modules/format/object/toCamelCaseKeys"
import { logger } from "@/modules/monitoring/logger"
import { NOTIFICATION_BANNER_COOKIE_NAME } from "@/modules/notificationBanner/constants"
import { isNotificationBannerVisible } from "@/modules/notificationBanner/server/utils/isNotificationBannerVisible"

const GET_NOTIFICATION_BANNER_ENDPOINT =
  "/jobteaser.notification_banner.v1/NotificationBannerService/GetNotificationBanner"

export type NotificationBanner = {
  endsAt: string
  idOld: string
  scope: string
  startsAt: string
  text: string
  title: string
  updatedAt: string
}

type GetNotificationBannerResponse = {
  notificationBanner: NotificationBanner
}

export type NotificationBannerRequest = {
  isBackend: boolean
  locale: I18nLocale
}

export type GetNotificationBannerParams = NotificationBannerRequest & {
  req?: NextRequest
}

export const getNotificationBanner = async ({
  isBackend = false,
  locale,
  req,
}: GetNotificationBannerParams): Promise<NotificationBanner | null> => {
  return fetcher(GET_NOTIFICATION_BANNER_ENDPOINT, {
    nextRequest: req,
    requestInit: {
      body: JSON.stringify({ is_backend: isBackend, locale }),
      method: "POST",
    },
    serviceDomain: "KRAKEND_API",
    storageKey: `notification-banner::${locale}::${isBackend}`,
  })
    .then(
      getResultFromResponse<GetNotificationBannerResponse>({
        transformResult: toCamelCaseKeys<GetNotificationBannerResponse>,
      })
    )
    .then(async data => {
      const notificationBanner = data?.notificationBanner

      if (!notificationBanner) return null

      /**
       * Check whether the notification banner should be displayed if the user has not dismissed it.
       */
      // If the request is made from the server, get cookies from Next headers.
      const cookieBannerValue =
        req && typeof window === "undefined"
          ? req.cookies[NOTIFICATION_BANNER_COOKIE_NAME]
          : getCookie(NOTIFICATION_BANNER_COOKIE_NAME)

      const isBannerVisible = isNotificationBannerVisible({ cookieBannerValue, notificationBanner })

      return isBannerVisible ? notificationBanner : null
    })
    .catch((error: Error): null => {
      // Don't throw an error to avoid breaking the app.
      logger.error(`An error occurred when fetching notification banner: ${ensureError(error).message}`)

      return null
    })
}
